import React, { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import useAuthentication from '../../../../customHooks/useAuthentication';
import { MixpanelContext, SCREENS } from '../../../../utils/mixpanel';
import Button from '../../../common/Button/Button';
import TextField from '../../../common/TextField/TextField';
import { useQueryParam } from '../../../../utils/AppContext';

interface Props {
  onSSOFailed: (email: string) => void;
  email: string;
  setErrorMessageKey: (error?: string) => void;
}

const EmailSSOForm: React.FC<Props> = ({ onSSOFailed, email, setErrorMessageKey }) => {
  const [currentEmail, setCurrentEmail] = useState(email);
  const [isButtonAnimating, setIsButtonAnimating] = useState(false);
  const { checkSSOEnabled } = useAuthentication();
  const { t } = useTranslation();
  const { sendMixpanelError, sendMixpanelEvent } = useContext(MixpanelContext);
  const returnJWTToken = useQueryParam('returnJWTToken') ?? 'false';

  const onEmailSent = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (currentEmail === '') {
      setErrorMessageKey('emailEmpty');
      sendMixpanelError({
        email: currentEmail,
        errorKey: 'emailEmpty',
        screen: SCREENS.EMAIL_FORM,
      });
    } else {
      setErrorMessageKey(undefined);
      setIsButtonAnimating(true);
      checkSSOEnabled(currentEmail, returnJWTToken)
        .then((res) => {
          if (res?.data?.sso_enabled) {
            sendMixpanelEvent('Redirecting to SSO', {
              email,
              redirectURL: res?.data?.login_url,
            });
            window.location.href = res?.data?.login_url;
          } else {
            setIsButtonAnimating(false);
            onSSOFailed(currentEmail);
          }
        })
        .catch((error) => {
          console.error('Error checking SSO', error);
          setIsButtonAnimating(false);
          // If domain is not sso enabled or its a local environment continue to password screen
          if (
            error?.response?.status === 404 ||
            process.env.NODE_ENV === 'development' ||
            window.location.hostname.includes('development')
          ) {
            onSSOFailed(currentEmail);
          } else {
            setErrorMessageKey('errors.unknown_sso');
            sendMixpanelError({
              axiosError: error,
              email: currentEmail,
              errorKey: 'errors.sso_unknown',
              screen: SCREENS.EMAIL_FORM,
            });
          }
        });
    }
  };

  const onInvalidEmail = () => {
    setErrorMessageKey('invalidEmail');
    sendMixpanelError({
      email: currentEmail,
      errorKey: 'invalidEmail',
      screen: SCREENS.EMAIL_FORM,
    });
  };

  return (
    <form className="email-form" onSubmit={onEmailSent}>
      <TextField
        value={currentEmail}
        label={t('email')}
        placeholder={t('emailPlaceholder')}
        onChange={(e) => setCurrentEmail(e.currentTarget.value)}
        id="email"
        type="email"
        onInvalid={onInvalidEmail}
      ></TextField>
      <Button text={t('next')} isAnimating={isButtonAnimating}></Button>
    </form>
  );
};

export default EmailSSOForm;
